/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GmLogo } from '@vaas/react-ui-kit';
import { Icon } from '@/components/Icon';
import { useCurrentUser } from '@/auth/useAccess';
import { workspacesFor } from '@/auth/workspaceRoutes';
import { cn } from '@/lib/cn';
import { prefetchRoute } from '@/routes/lazyRoutes';
import { useUiStore } from '@/stores/uiStore';
import { navBottom, navTop, type NavItem } from './nav';
import { useRail } from './useRail';

/**
 * Keeps the rail to the destinations the workspace grants actually reach.
 *
 * <p>Read from the same map the route guard reads, so an icon is offered exactly when following
 * it would arrive somewhere. Fails closed while the grants are still resolving: a rail that shows
 * everything for a moment and then takes half of it away is worse than one that fills in.
 */
function useVisibleNav(items: NavItem[]): NavItem[] {
  const { data } = useCurrentUser();
  const held = data?.workspaces ?? [];
  return items.filter((item) => {
    const required = workspacesFor(item.to);
    return !required || required.some((workspace) => held.includes(workspace));
  });
}

/*
 * Rail entries name themselves by tooltip only while the rail is slim. `data-tip` rather than
 * `title`: the tooltip layer moves a `title` into `data-tip` itself the first time it shows, and
 * React could never take back an attribute it did not set.
 */

function RailButton({ item, named }: { item: NavItem; named: boolean }) {
  const { t } = useTranslation();
  const remembered = useUiStore((s) => s.routeMemory[item.to]);
  const warm = () => prefetchRoute(item.to);
  // Restore last URL for this tool; query string doesn't affect NavLink active state.
  const to = remembered ?? item.to;
  const label = t(`nav.${item.key}`, item.label);
  return (
    <NavLink
      to={to}
      end={item.end}
      data-tip={named ? undefined : label}
      aria-label={label}
      data-tour={`nav-${item.key}`}
      onMouseEnter={warm}
      onFocus={warm}
      className={({ isActive }) => cn('rb', isActive && 'on')}
    >
      <Icon name={item.icon} size={22} />
      <span className="rb-label" aria-hidden="true">
        {label}
      </span>
    </NavLink>
  );
}

/** Rail entry that opens a global dialog instead of navigating. */
function RailAction({
  item,
  named,
  onClick,
  active,
}: {
  item: NavItem;
  named: boolean;
  onClick: () => void;
  active?: boolean;
}) {
  const { t } = useTranslation();
  const label = t(`nav.${item.key}`, item.label);
  return (
    <button
      type="button"
      data-tip={named ? undefined : label}
      aria-label={label}
      data-tour={`nav-${item.key}`}
      onClick={onClick}
      className={cn('rb', active && 'on')}
    >
      <Icon name={item.icon} size={22} />
      <span className="rb-label" aria-hidden="true">
        {label}
      </span>
    </button>
  );
}

/** Id of the rail, for the topbar burger's `aria-controls`. */
export const RAIL_ID = 'app-rail';

/**
 * Slim icon navigation rail; the arrow at its foot widens it to name every destination. Below
 * 600px it is an off-canvas drawer with labels, opened by the topbar's menu button: `open` slides
 * it in over a scrim, and every way out (the close button, the scrim, Escape, choosing a
 * destination) calls `onClose`. Wider screens ignore `open`.
 */
export function Rail({ open = false, onClose }: { open?: boolean; onClose?: () => void }) {
  const { t } = useTranslation();
  const resourcesOpen = useUiStore((s) => s.resourcesOpen);
  const openResources = useUiStore((s) => s.openResources);
  const setRailPeek = useUiStore((s) => s.setRailPeek);
  const rail = useRail();
  const visibleTop = useVisibleNav(navTop);
  const visibleBottom = useVisibleNav(navBottom);
  const railRef = useRef<HTMLElement>(null);
  const close = () => {
    onClose?.();
    setRailPeek(false);
  };

  // Open over the page, the rail goes the way a popover does: a pick, a click anywhere else,
  // Escape, or keyboard focus moving on into the page.
  useEffect(() => {
    if (!rail.over) return;
    const aside = railRef.current as HTMLElement;
    const shut = () => setRailPeek(false);
    const onDown = (e: PointerEvent) => {
      if (!aside.contains(e.target as Node)) shut();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') shut();
    };
    // Only a move to somewhere else: clicking the rail's own empty space blurs to nothing.
    const onFocusOut = (e: FocusEvent) => {
      const to = e.relatedTarget as Node | null;
      if (to && !aside.contains(to)) shut();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    aside.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      aside.removeEventListener('focusout', onFocusOut);
    };
  }, [rail.over, setRailPeek]);

  // While the drawer is open: Escape closes it, and focus moves into it (and back to the menu
  // button when it closes, if it was still inside).
  useEffect(() => {
    if (!open) return;
    const rail = railRef.current;
    rail?.querySelector<HTMLElement>('.rail-close')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (rail?.contains(document.activeElement)) {
        document.querySelector<HTMLElement>(`[aria-controls="${RAIL_ID}"]`)?.focus();
      }
    };
  }, [open, onClose]);

  const toggleLabel = rail.open ? t('nav.collapseSidebar') : t('nav.expandSidebar');
  return (
    <>
      <aside
        id={RAIL_ID}
        ref={railRef}
        className="rail"
        data-tour="rail"
        aria-label={t('nav.primary')}
        // Choosing any destination closes the drawer; harmless when it is not a drawer.
        onClick={(e) => {
          if ((e.target as Element).closest('.rb')) close();
        }}
      >
        <div className="rail-logo">
          <GmLogo className="gm-badge" size={34} decorative />
          <span className="rail-logo-text">{t('topbar.brandTitle')}</span>
        </div>
        <button
          type="button"
          className="rail-close"
          onClick={close}
          aria-label={t('nav.closeMenu')}
          title={t('nav.closeMenu')}
        >
          <Icon name="close" size={20} />
        </button>
        {visibleTop.map((item) =>
          item.key === 'resources' ? (
            <RailAction
              key={item.key}
              item={item}
              named={rail.open}
              onClick={openResources}
              active={resourcesOpen}
            />
          ) : (
            <RailButton key={item.key} item={item} named={rail.open} />
          ),
        )}
        <div className="rail-sp" />
        {visibleBottom.map((item) => (
          <RailButton key={item.key} item={item} named={rail.open} />
        ))}
        <button
          type="button"
          className="rail-toggle"
          data-tip={toggleLabel}
          aria-label={toggleLabel}
          aria-expanded={rail.open}
          aria-controls={RAIL_ID}
          onClick={rail.toggle}
        >
          <Icon name={rail.open ? 'arrow_back' : 'arrow_forward'} size={20} />
        </button>
      </aside>
      {open && (
        <button
          type="button"
          className="rail-backdrop"
          onClick={close}
          aria-label={t('nav.closeMenu')}
          tabIndex={-1}
        />
      )}
    </>
  );
}
